import { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import axiosInstance from '../../../../../utils/axios';
import { updateAllCartsItemsCount, updateCartInfoItems } from '../../../../../redux/slices/orderSheet';
import { objectToFormData } from '../../../../../api/utils';
import { useDispatch } from '../../../../../redux/store';

const CartsContext = createContext({});

const toCartInfoItems = (carts) => carts.map(cart => ({
  items: cart.items.map(i => ({...i, itemId: i.itemId?.toString()})),
  categoryId: cart.categoryId,
  categoryName: cart.category,
  itemCount: cart.items?.length ?? 0,
  icon: cart.icon,
}));

function CartsProvider({ facilityId, categoryId, setUrlParams, children })
{
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [carts, setCarts] = useState([]);
  const [facilityName, setFacilityName] = useState(null);
  const [bulkDeleteItems, setBulkDeleteItems] = useState(null);
  const [personForOptions, setPersonForOptions] = useState([]);
  const [personFor, setPersonFor] = useState(null);
  const [checkoutCartItems, setCheckoutCartItems] = useState(null);
  const [checkoutCartCategory, setCheckoutCartCategory] = useState(null);
  const [requestedDate, setRequestedDate] = useState(null);
  const [submittingCart, setSubmittingCart] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [totals, setTotals] = useState({ mainTotal: { price: 0, items: 0, errors: 0 } });

  useEffect(() => {
    setCarts([]);
    setLoading(!!facilityId);
    if (facilityId) {
      axiosInstance.get(`/FacilityCart?facilityId=${facilityId}`)
        .then(res => { 
          setCartsWithTotals(res.data.carts ?? []);
          setFacilityName(res.data.facilityName);
          setPersonForOptions(res.data.personForOptions?.map((val) => ({ value: val.email, label: `${val.name} - ${val.email}`, name: val.name })) ?? []);
          setPersonFor(res.data.defaultPersonFor);
          setUnsavedChanges(false);
          setBulkDeleteItems(null);     
          setRequestedDate(null);
          setCheckoutData(categoryId, res.data.carts ?? [])
        })
        .catch(() => enqueueSnackbar("Fetch facility cart failed", { variant: "error" }))
        .finally(() => setLoading(false));
    }
  }, [facilityId]);

  const setCheckoutData = (categoryId, carts) => {
    if (categoryId) {
      setCheckoutCartCategory({ categoryId, category: categoryId === -1 ? "All" : carts.find(cart => cart?.categoryId === categoryId)?.category ?? "Unknown" });
      setCheckoutCartItems(carts.filter(cart => (categoryId === -1 || cart.categoryId === categoryId) && cart.orderable && cart.items?.length > 0).flatMap(cart => cart.items));
    } else {
      setCheckoutCartCategory(null);
      setCheckoutCartItems(null);
    }
  }

  useEffect(() => { setCheckoutData(categoryId, carts); }, [categoryId, carts]);

  const setCartsWithTotals = (carts) => {
    const totals = { mainTotal: { price: 0, items: 0, errors: 0 } };
    carts.filter(cart => cart.orderable && cart.items?.length > 0).forEach((cart) => {
      totals[cart.categoryId] = { totalPrice: 0, items: [], errorCount: 0 };
      cart.items.forEach((item) => {     
        if (item.productUOMs?.length > 0) item.uomAmount = item.productUOMs.find(uom => uom.uomType === item.uomType)?.uomAmount ?? item.uomAmount;
        totals[cart.categoryId].totalPrice += item.price * item.quantity;
        totals[cart.categoryId].items.push(item);
      });
      totals.mainTotal.price += totals[cart.categoryId].totalPrice;
      totals.mainTotal.items += totals[cart.categoryId].items.length;
    });
    setTotals(totals);
    setCarts(carts);
  };

  const updateCartItemFields = (id, fields) => {
    const items = carts.flatMap(cart => cart.items);
    const item = items.find(item => item.id === id);
    fields.forEach(field => {
      if (field.field === "quantity" && item.origQty === undefined) item.origQty = item.quantity;
      if (field.field === "uomType" && item.origUOMType === undefined) item.origUOMType = item.uomType;
      item[field.field] = field.value;
    });
    setUnsavedChanges(items.some(item => item.origQty && item.origQty !== item.quantity || item.origUOMType && item.origUOMType !== item.uomType));
    setCartsWithTotals(carts);
  }

  const setBulkDeleteItemsByIndex = (items, index) => {
    setBulkDeleteItems(bulkDelete => {
      if (!bulkDelete || !items) return null;     
      bulkDelete[index] = items;
      return { ...bulkDelete };
    })
  }

  const deleteItem = (id, itemId, punchoutItemId, punchoutId, punchoutLocked, lastItem, setDeleting) => {
    setDeleting(true);
    axiosInstance.post(`/DeleteCartItem?facilityId=${facilityId}&itemId=${itemId}&punchoutId=${punchoutItemId}`)
      .then(() => {
        dispatch(updateAllCartsItemsCount(-carts.flatMap(cart => cart.items?.filter(item => punchoutLocked ? item.punchoutId === punchoutId : item.id === id) ?? [])
          .reduce((acc, item) => acc + (item.origQty ?? item.quantity), 0)));
        if (lastItem) navigate("/dashboard/orders?filter=pending");
        else {
          carts.forEach(cart => { cart.items = cart.items?.filter(item => punchoutLocked ? item.punchoutId !== punchoutId : item.id !== id) });
          setCartsWithTotals([...carts]);
          setDeleting(false);
        }    
      })
      .catch(() => { enqueueSnackbar("Item failed to delete", { variant: "error" }); setDeleting(false); });
  };

  const setCheckoutCart = (categoryId) => setUrlParams({ categoryId });

  const submitCart = () => {

    const formData = objectToFormData({    
      facilityId,
      personFor,
      requestedDate,
      hasPunchoutCart: checkoutCartItems?.some(item => item.punchoutItemId > 0),
      categoryId: checkoutCartCategory?.categoryId === -1 ? null : checkoutCartCategory?.categoryId 
    });
    setSubmittingCart(true);
    axiosInstance.post('/SubmitOrder', formData)
      .then((res) => {
        dispatch(updateAllCartsItemsCount(-(checkoutCartItems ?? []).reduce((acc, item) => acc + (item.origQty ?? item.quantity), 0)));
        if (res.data.success) {
          const submittedItemIds = (checkoutCartItems ?? []).map(item => item.id);
          const cartInfoItems = toCartInfoItems(carts.map(cart => ({ ...cart, items: cart.items?.filter(item => !submittedItemIds.includes(item.id)) ?? [] }))).filter(c => c.items.length > 0);
          dispatch(updateCartInfoItems(cartInfoItems));
          navigate(res.data.message);
        }
        else {
          enqueueSnackbar(res.data.message, { variant: 'error' });
          setSubmittingCart(false);
        }
      })
      .catch(() => {
        enqueueSnackbar("Something has gone wrong", { variant: 'error' });
        setSubmittingCart(false);
      });
  };

  // ensure cartInfo in order sheet redux is updated to reflect the deleted item(s)
  useEffect(() => {
    dispatch(updateCartInfoItems(toCartInfoItems(carts)));
  }, [carts, dispatch]);

  return <CartsContext.Provider value={{
    carts, setCartsWithTotals, updateCartItemFields, loading, facilityName, facilityId, totals, bulkDeleteItems, setBulkDeleteItems, setBulkDeleteItemsByIndex,
    submittingCart, deleteItem, personForOptions, setPersonFor, personFor, checkoutCartItems, setCheckoutCart, requestedDate, setRequestedDate,
    submitCart, checkoutCartCategory, unsavedChanges, setUnsavedChanges
  }}>
    {children}
  </CartsContext.Provider>;
}

export { CartsContext, CartsProvider };
